import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export const PASSWORD_MIN_LENGTH = 12;

// The set of characters AWS Cognito accepts as a special character. Keeping the
// client check aligned with (or stricter than) the pool policy means anything the
// user is allowed to submit is also accepted by Cognito.
const SPECIAL_CHARACTERS = '^$*.[]{}()?"!@#%&/\\,><\':;|_~`+-=';

// The rules live here and only here. Adding an entry is picked up automatically by
// both the form validator and the checklist, and widens PasswordRequirementId so
// the compiler demands a matching translated label.
export const PASSWORD_REQUIREMENTS = [
  {
    id: 'minLength',
    test: (password: string) => password.length >= PASSWORD_MIN_LENGTH,
  },
  {
    id: 'uppercase',
    test: (password: string) => /[A-Z]/.test(password),
  },
  {
    id: 'lowercase',
    test: (password: string) => /[a-z]/.test(password),
  },
  {
    id: 'number',
    test: (password: string) => /[0-9]/.test(password),
  },
  {
    id: 'symbol',
    test: (password: string) =>
      Array.from(password).some((character) =>
        SPECIAL_CHARACTERS.includes(character),
      ),
  },
] as const;

export type PasswordRequirementId = (typeof PASSWORD_REQUIREMENTS)[number]['id'];

function getUnmetPasswordRequirements(
  password: string | null | undefined,
): PasswordRequirementId[] {
  const value = password ?? '';

  return PASSWORD_REQUIREMENTS.filter(
    (requirement) => !requirement.test(value),
  ).map((requirement) => requirement.id);
}

export function passwordRequirementsValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const unmet = getUnmetPasswordRequirements(control.value);

    return unmet.length ? { passwordRequirements: unmet } : null;
  };
}
